import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // The auth token lives only in a secure HTTPOnly cookie set by the backend,
  // so on every app load we ask the server who we are (the cookie is sent
  // automatically). This avoids ever storing the JWT in localStorage/JS.
  useEffect(() => {
    api
      .get("/auth/me")
      .then((res) => {
        setUser({ ...res.data.user, profile: res.data.profile });
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user?.role) {
      document.documentElement.setAttribute("data-role", user.role);
    } else {
      document.documentElement.removeAttribute("data-role");
    }
  }, [user]);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const res = await api.post("/auth/register", payload);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Failed to clear session on server", err);
    } finally {
      setUser(null);
      window.location.href = "/login";
    }
  }, []);

  // Memoized so components consuming useAuth() only re-render when one of
  // these values actually changes, instead of on every AuthProvider
  // render (which would otherwise happen because `login`/`register` were
  // plain arrow functions re-created on each render).
  const value = useMemo(
    () => ({ user, setUser, login, register, logout, loading }),
    [user, login, register, logout, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
